import { networkAPI } from 'api/NetworkAPI'
import { userAPI } from 'api/UserAPI'
import { useEffect, useState } from 'react'
import { IError } from 'types/error'
import { ICombinedNetworkMember, INetwork } from 'types/networkInterfaces'
import { useUser } from './user'

let brokerNetworksCache: { data: INetwork[]; timestamp: number } | null = null
export const clearBrokerNetworksCache = () => {
  brokerNetworksCache = null
}
const BROKER_NETWORKS_CACHE_DURATION = 5 * 60 * 1000 // 5 minutes

/**
 * Hook for getting networks.
 *
 * @returns Hook variables and functions.
 */
export const useNetworks = () => {
  const [networks, setNetworks] = useState<INetwork[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<IError>()

  const { user } = useUser()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        const results = await networkAPI.getNetworks(controller)
        setNetworks(results)
      } catch (error) {
        setError(error as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [user])

  return {
    loading,
    networks,
    error,
  }
}

/**
 * Hook for getting account networks.
 *
 * @param accountId - Account ID.
 * @returns Hook variables and functions.
 */
export const useAccountNetworks = (accountId?: number) => {
  const [networks, setNetworks] = useState<INetwork[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (accountId) {
        try {
          setLoading(true)
          const results = await networkAPI.getAccountNetworks(accountId, controller)
          setNetworks(results)
        } catch (error) {
          setError(error as IError)
        } finally {
          setLoading(false)
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [accountId])

  return {
    loading,
    networks,
    error,
  }
}

/**
 * Hook for getting manager accessible networks.
 *
 * @returns Hook variables and functions.
 */
export const useManagerNetworks = () => {
  const [networks, setNetworks] = useState<INetwork[]>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<IError>()

  const { user } = useUser()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        const results = await networkAPI.getManagerAccessNetworks(controller)
        setNetworks(results)
      } catch (error) {
        setError(error as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [user])

  return {
    loading,
    networks,
    error,
  }
}

/**
 * Hook for getting broker accessible networks with caching.
 *
 * @returns Hook variables and functions.
 */
export const useBrokerNetworks = () => {
  const [networks, setNetworks] = useState<INetwork[] | undefined>(() => {
    // Try to load cached networks immediately
    if (brokerNetworksCache && Date.now() - brokerNetworksCache.timestamp < BROKER_NETWORKS_CACHE_DURATION) {
      return brokerNetworksCache.data
    }
    return undefined
  })
  const [loading, setLoading] = useState(!networks)
  const [error, setError] = useState<IError>()

  const { user } = useUser()

  useEffect(() => {
    const controller = new AbortController()
    let stillValid = true

    // Check if we have valid cached data
    const hasValidCache =
      brokerNetworksCache && Date.now() - brokerNetworksCache.timestamp < BROKER_NETWORKS_CACHE_DURATION

    ;(async () => {
      try {
        // Only show a loading indicator when there is no cached data; when
        // cached networks exist we keep them for the initial render but still
        // refresh in the background so errors and updates are surfaced.
        if (!hasValidCache) {
          setLoading(true)
        }

        if (!user) {
          // Prevent leaking previously cached networks to an unauthenticated
          // view by caching an explicit empty result for the duration. A
          // subsequent authenticated session will revalidate in the
          // background and replace this cache.
          setNetworks([])
          brokerNetworksCache = { data: [], timestamp: Date.now() }
          setLoading(false)
          return
        }

        // Always refresh from the API (background refresh when cache exists).
        const results = await networkAPI.getBrokerAccessNetworks(controller)

        if (stillValid) {
          setNetworks(results)
          // Cache the results
          brokerNetworksCache = {
            data: results,
            timestamp: Date.now(),
          }
        }
      } catch (error) {
        if ((error as IError)?.name !== 'AbortError' && (error as IError)?.name !== 'CanceledError') {
          setError(error as IError)
          // Ensure tests waiting on loading becoming false will observe
          // the error synchronously with the loading state transition.
          setLoading(false)
        }
      } finally {
        if (stillValid) {
          setLoading(false)
        }
      }
    })()

    return () => {
      controller.abort()
      stillValid = false
    }
  }, [user])

  return {
    loading,
    networks,
    error,
  }
}

/**
 * Hook for getting searchable networks with caching.
 *
 * @returns Hook variables and functions.
 */
export const useSearchableNetworks = () => {
  const [networks, setNetworks] = useState<INetwork[]>(() => {
    // Try to load cached networks immediately
    try {
      const cached = sessionStorage.getItem('caleo_searchable_networks')
      if (cached) {
        const { data, timestamp } = JSON.parse(cached)
        // Cache valid for 5 minutes
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return data
        }
      }
    } catch (error) {
      console.error('Error loading cached searchable networks:', error)
    }
    return undefined
  })
  const [loading, setLoading] = useState(!networks)
  const [error, setError] = useState<IError>()

  const { user } = useUser()

  useEffect(() => {
    const controller = new AbortController()
    let stillValid = true

    // Check if we have valid cached data
    let hasValidCache = false
    try {
      const cached = sessionStorage.getItem('caleo_searchable_networks')
      if (cached) {
        const { timestamp } = JSON.parse(cached)
        // Cache valid for 5 minutes
        hasValidCache = Date.now() - timestamp < 5 * 60 * 1000
      }
    } catch (error) {
      console.error('Error checking cached searchable networks:', error)
    }

    ;(async () => {
      try {
        if (!hasValidCache) {
          setLoading(true)
        }

        if (!user) {
          setNetworks([])
          setLoading(false)
          return
        }

        const fetchedNetworks = await networkAPI.getSearchableNetworks(controller)

        if (stillValid) {
          setNetworks(fetchedNetworks)
          // Cache the results in sessionStorage
          try {
            sessionStorage.setItem(
              'caleo_searchable_networks',
              JSON.stringify({
                data: fetchedNetworks,
                timestamp: Date.now(),
              })
            )
          } catch (error) {
            console.error('Error caching searchable networks:', error)
          }
        }
      } catch (error) {
        if ((error as IError)?.name !== 'AbortError' && (error as IError)?.name !== 'CanceledError') {
          setError(error as IError)
        }
      } finally {
        if (stillValid) {
          setLoading(false)
        }
      }
    })()

    return () => {
      controller.abort()
      stillValid = false
    }
  }, [user])

  return {
    loading,
    networks,
    error,
  }
}

/**
 * Hook for getting network statistics.
 *
 * @param networkId - Network ID.
 * @returns Hook variables and functions.
 */
export const useNetworkStatistics = (networkId: number) => {
  const [combinedNetworkMembers, setCombinedNetworkMembers] = useState<ICombinedNetworkMember[]>()
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<IError>()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        const results = await networkAPI.getNetworkStatistics(networkId, controller)
        setCombinedNetworkMembers(results)
      } catch (error) {
        setError(error as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [networkId])

  return {
    combinedNetworkMembers,
    loading,
    error,
  }
}

/**
 * Hook for getting sales networks.
 *
 * @returns Hook variables and functions.
 */
export const useSalesNetworks = () => {
  const [networks, setNetworks] = useState<INetwork[]>()
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<IError>()

  const { user, groups } = useUser()

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      try {
        setLoading(true)
        if (groups && !groups.includes('freelancer')) {
          const results = await userAPI.getSalesNetworks(controller)
          setNetworks(results)
        }
      } catch (error) {
        setError(error as IError)
      } finally {
        setLoading(false)
      }
    })()

    return () => {
      controller.abort()
    }
  }, [groups, user])

  return {
    loading,
    networks,
    error,
  }
}
